import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import { Coffee, Utensils, Pizza, Cookie, Plus, Pencil, Trash2, Eye, EyeOff, Check, Loader2, Search, Bike, X } from 'lucide-react';
import toast from 'react-hot-toast';
import axiosInstance from '../utils/axiosConfig';
import { tienePrecio, formatoMonto } from '../utils/menuComida';

const TURNOS = [
    { id: 'Desayuno', icon: Coffee, color: 'bg-amber-500' },
    { id: 'Almuerzo', icon: Utensils, color: 'bg-emerald-500' },
    { id: 'Cena', icon: Pizza, color: 'bg-sura-blue' },
    { id: 'Refrigerio', icon: Cookie, color: 'bg-rose-500' },
];

const SECCIONES = {
    Desayuno: [
        { tipo: 'pieza', titulo: 'Piezas', ayuda: 'Empanada, pastelito, arepa... Cada desayuno es de un solo tipo', singular: 'pieza' },
        { tipo: 'relleno', titulo: 'Rellenos', ayuda: 'Para cualquiera de las piezas. No llevan precio: el precio va en la pieza', singular: 'relleno' },
        { tipo: 'bebida', titulo: 'Bebidas', ayuda: 'Se elige una por desayuno', singular: 'bebida' },
    ],
    Almuerzo: [
        { tipo: 'plato', titulo: 'Comida principal', ayuda: 'Agrupada por categoría', singular: 'plato' },
        { tipo: 'contorno', titulo: 'Contornos', ayuda: 'Cada empleado puede elegir hasta 2. No llevan precio: van incluidos en el precio del plato', singular: 'contorno' },
        { tipo: 'bebida', titulo: 'Bebidas', ayuda: 'Opcional al pedir', singular: 'bebida' },
    ],
    Cena: [
        { tipo: 'plato', titulo: 'Comida principal', ayuda: 'Agrupada por categoría, con sus ingredientes', singular: 'plato' },
        { tipo: 'bebida', titulo: 'Bebidas', ayuda: 'Opcional al pedir', singular: 'bebida' },
    ],
    Refrigerio: [
        { tipo: 'refrigerio', titulo: 'Refrigerios', ayuda: 'Para eventos. El precio va en cada combo (unidades + precio). Quien pide solo ve el nombre; los combos los usa Procura al armar el pedido', singular: 'refrigerio' },
    ],
};

const INPUT = 'w-full px-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-gray-50 focus:border-sura-blue';
const BTN_PRIMARIO = 'flex items-center gap-2 px-4 py-2.5 bg-sura-blue text-white rounded-xl font-medium hover:bg-sura-blue-dark transition-colors text-sm disabled:opacity-50';
const BTN_ICONO = 'p-1.5 hover:bg-gray-100 rounded-lg text-gray-400 transition-colors disabled:opacity-50';

const normalizar = (t) => (t || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();

const descripcionLabel = (turno, tipo) => {
    if (tipo === 'plato' && turno === 'Cena') return 'Ingredientes';
    if (tipo === 'bebida') return 'Detalle (ej. Varios sabores)';
    if (tipo === 'refrigerio') return 'Descripción (opcional)';
    return null;
};

const StatCard = ({ title, value, subtext, icon: Icon, color, onClick, active }) => (
    <div onClick={onClick} className={`bg-white p-6 rounded-3xl border ${active ? 'border-sura-blue shadow-md ring-2 ring-sura-blue/10' : 'border-gray-100 shadow-sm'} cursor-pointer hover:shadow-md transition-all flex items-center gap-4`}>
        <div className={`p-4 rounded-2xl ${color}`}>
            <Icon size={24} className="text-white" />
        </div>
        <div>
            <p className="text-sm text-gray-500 font-medium">{title}</p>
            <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
            {subtext && <p className="text-xs text-sura-blue">{subtext}</p>}
        </div>
    </div>
);

// Solo estos llevan precio. Contornos y rellenos no: van incluidos en el plato o la pieza.
// Los refrigerios llevan el precio en sus combos: les "falta precio" si no tienen combos.
const TIPOS_CON_PRECIO = ['plato', 'pieza', 'bebida'];
const sinCombos = (item) => item.tipo === 'refrigerio' && !(item.combos || []).length;
const faltaPrecio = (item) => sinCombos(item) || (TIPOS_CON_PRECIO.includes(item.tipo) && !tienePrecio(item));

// "12 und · $ 5,00"
const textoCombo = (c) => `${c.unidades} und · ${formatoMonto(c.precio)}`;

// Combos de un refrigerio (unidades + precio): filas editables. Las vacías se ignoran al guardar.
const EditorCombos = ({ combos, onChange }) => {
    const cambiar = (i, campo, valor) => onChange(combos.map((c, j) => (j === i ? { ...c, [campo]: valor } : c)));
    return (
        <div>
            <label className="block text-xs text-sura-blue mb-1">Combos (unidades y precio en USD)</label>
            <div className="space-y-2">
                {combos.map((c, i) => (
                    <div key={i} className="flex items-center gap-2">
                        <div className="relative w-32">
                            <input type="number" inputMode="numeric" min="1" max="1000" step="1"
                                value={c.unidades} onChange={e => cambiar(i, 'unidades', e.target.value)}
                                placeholder="Unidades" className={`${INPUT} pr-11`} aria-label="Unidades del combo" />
                            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">und</span>
                        </div>
                        <div className="relative w-32">
                            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">$</span>
                            <input type="number" inputMode="decimal" min="0" max="10000" step="any"
                                value={c.precio} onChange={e => cambiar(i, 'precio', e.target.value)}
                                placeholder="Precio" className={`${INPUT} pl-7`} aria-label="Precio del combo" />
                        </div>
                        <button type="button" onClick={() => onChange(combos.filter((_, j) => j !== i))}
                            className={`${BTN_ICONO} hover:text-red-500`} title="Quitar combo">
                            <X size={14} />
                        </button>
                    </div>
                ))}
            </div>
            <button type="button" onClick={() => onChange([...combos, { unidades: '', precio: '' }])}
                className="mt-2 text-xs text-sura-blue hover:underline flex items-center gap-1">
                <Plus size={12} /> Agregar combo
            </button>
        </div>
    );
};

// Valida las filas del editor. Devuelve { combos } o { error }.
const combosParaGuardar = (filas) => {
    const combos = [];
    for (const f of filas) {
        const u = String(f.unidades ?? '').trim();
        const p = String(f.precio ?? '').trim().replace(',', '.');
        if (u === '' && p === '') continue;
        const unidades = Number(u);
        const precio = Number(p);
        if (!Number.isInteger(unidades) || unidades < 1 || unidades > 1000) return { error: 'Las unidades de cada combo deben ser un número entero entre 1 y 1.000' };
        if (p === '' || !Number.isFinite(precio) || precio < 0 || precio > 10000) return { error: `El combo de ${unidades} und necesita un precio entre 0 y 10.000` };
        if (combos.some(c => c.unidades === unidades)) return { error: `Hay dos combos de ${unidades} und` };
        combos.push({ unidades, precio });
    }
    return { combos };
};

const ItemForm = ({ turno, tipo, categorias, inicial, guardando, onGuardar, onCancelar }) => {
    const [nombre, setNombre] = useState(inicial?.nombre || '');
    const [categoria, setCategoria] = useState(inicial?.categoria || '');
    const [descripcion, setDescripcion] = useState(inicial?.descripcion || '');
    const [precio, setPrecio] = useState(tienePrecio(inicial) ? String(inicial.precio) : '');
    const [combos, setCombos] = useState(() => (inicial?.combos || []).map(c => ({ unidades: String(c.unidades), precio: String(c.precio) })));
    const llevaPrecio = TIPOS_CON_PRECIO.includes(tipo);
    const esRefrigerio = tipo === 'refrigerio';
    const labelDescripcion = descripcionLabel(turno, tipo);
    const listaId = `categorias-${turno}`;

    const enviar = (e) => {
        e.preventDefault();
        if (esRefrigerio) {
            const { combos: limpios, error } = combosParaGuardar(combos);
            if (error) {
                toast.error(error);
                return;
            }
            onGuardar({ nombre, descripcion, combos: limpios });
            return;
        }
        const texto = llevaPrecio ? precio.trim().replace(',', '.') : '';
        if (texto !== '') {
            const n = Number(texto);
            if (!Number.isFinite(n) || n < 0 || n > 10000) {
                toast.error('El precio debe ser un número entre 0 y 10.000');
                return;
            }
        }
        onGuardar({ nombre, categoria, descripcion, precio: texto === '' ? null : Number(texto) });
    };

    return (
        <form onSubmit={enviar}
            className="col-span-full p-4 bg-gray-50 rounded-xl border border-gray-100 space-y-3">
            <div className={`grid grid-cols-1 ${tipo === 'plato' ? 'md:grid-cols-[1fr_1fr_9rem]' : llevaPrecio ? 'md:grid-cols-[1fr_9rem]' : ''} gap-4`}>
                <div>
                    <label className="block text-xs text-sura-blue mb-1">Nombre</label>
                    <input autoFocus value={nombre} onChange={e => setNombre(e.target.value)} maxLength={120} className={INPUT} required />
                </div>
                {tipo === 'plato' && (
                    <div>
                        <label className="block text-xs text-sura-blue mb-1">Categoría (elige una o escribe una nueva)</label>
                        <input list={listaId} value={categoria} onChange={e => setCategoria(e.target.value)} maxLength={60} className={INPUT} required />
                        <datalist id={listaId}>
                            {categorias.map(c => <option key={c} value={c} />)}
                        </datalist>
                    </div>
                )}
                {llevaPrecio && (
                <div>
                    <label className="block text-xs text-sura-blue mb-1">Precio (USD)</label>
                    <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">$</span>
                        <input type="number" inputMode="decimal" min="0" max="10000" step="any"
                            value={precio} onChange={e => setPrecio(e.target.value)}
                            placeholder="Sin precio" className={`${INPUT} pl-7`} />
                    </div>
                </div>
                )}
            </div>
            {labelDescripcion && (
                <div>
                    <label className="block text-xs text-sura-blue mb-1">{labelDescripcion}</label>
                    <input value={descripcion} onChange={e => setDescripcion(e.target.value)} className={INPUT} />
                </div>
            )}
            {esRefrigerio && <EditorCombos combos={combos} onChange={setCombos} />}
            <div className="flex justify-end gap-2">
                <button type="button" onClick={onCancelar} className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg">Cancelar</button>
                <button type="submit" disabled={guardando}
                    className="px-4 py-2 text-sm bg-sura-blue text-white rounded-xl hover:bg-sura-blue-dark disabled:opacity-50 flex items-center gap-2">
                    {guardando ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Guardar
                </button>
            </div>
        </form>
    );
};

// Precio de cada delivery (USD). Procura indica cuántos deliveries hubo al cerrar el
// pedido del día en Reportes de Comida y se cobra con el precio vigente en ese momento.
const PrecioDelivery = () => {
    const [precio, setPrecio] = useState('');
    const [guardado, setGuardado] = useState(null);
    const [guardando, setGuardando] = useState(false);

    useEffect(() => {
        axiosInstance.get('/api/comidas/delivery-precio')
            .then(res => { setGuardado(res.data.precio); setPrecio(String(res.data.precio)); })
            .catch(() => toast.error('No se pudo cargar el precio del delivery'));
    }, []);

    const texto = precio.trim().replace(',', '.');
    const cambio = guardado !== null && texto !== '' && Number(texto) !== Number(guardado);

    const guardar = async (e) => {
        e.preventDefault();
        const n = Number(texto);
        if (texto === '' || !Number.isFinite(n) || n < 0 || n > 1000) {
            toast.error('El precio del delivery debe ser un número entre 0 y 1.000');
            return;
        }
        setGuardando(true);
        try {
            const res = await axiosInstance.put('/api/comidas/delivery-precio', { precio: n });
            setGuardado(res.data.precio);
            setPrecio(String(res.data.precio));
            toast.success('Precio del delivery actualizado');
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudo guardar el precio');
        } finally {
            setGuardando(false);
        }
    };

    return (
        <form onSubmit={guardar} className="flex flex-wrap items-center gap-3 bg-white border border-gray-100 rounded-2xl px-5 py-4">
            <div className="flex items-center gap-3 min-w-0 flex-1">
                <div className="p-2 rounded-xl bg-gray-50 shrink-0"><Bike size={18} className="text-sura-blue" /></div>
                <div className="min-w-0">
                    <p className="text-sm font-semibold text-gray-900">Precio del delivery (USD)</p>
                    <p className="text-xs text-gray-500">Se usa al cerrar el pedido del día en Reportes de Comida. Cambiarlo no afecta los turnos ya cerrados.</p>
                </div>
            </div>
            <div className="flex items-center gap-2">
                <div className="relative w-28">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-gray-400">$</span>
                    <input type="number" inputMode="decimal" min="0" max="1000" step="any"
                        value={precio} onChange={e => setPrecio(e.target.value)}
                        disabled={guardado === null} className={`${INPUT} pl-7`} />
                </div>
                <button type="submit" disabled={!cambio || guardando}
                    className="px-4 py-2 text-sm bg-sura-blue text-white rounded-xl hover:bg-sura-blue-dark disabled:opacity-50 flex items-center gap-2">
                    {guardando ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />} Guardar
                </button>
            </div>
        </form>
    );
};

const MenuComidaAdmin = ({ onChange }) => {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [turno, setTurno] = useState('Almuerzo');
    const [busqueda, setBusqueda] = useState('');
    const [editandoId, setEditandoId] = useState(null);
    const [agregando, setAgregando] = useState(null); // { tipo, categoria }
    const [guardando, setGuardando] = useState(false);
    const [soloSinPrecio, setSoloSinPrecio] = useState(false);

    const cargar = async () => {
        try {
            const res = await axiosInstance.get('/api/comidas/menu?todos=1');
            setItems(res.data);
        } catch (err) {
            toast.error('No se pudo cargar el menú');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        cargar();
    }, []);

    const resumen = useMemo(() => {
        const r = {};
        TURNOS.forEach(t => {
            const delTurno = items.filter(i => i.turno === t.id);
            r[t.id] = {
                total: delTurno.length,
                ocultos: delTurno.filter(i => !i.activo || i.categoria_oculta).length,
                sinPrecio: delTurno.filter(faltaPrecio).length,
            };
        });
        return r;
    }, [items]);

    const cerrarFormularios = () => { setEditandoId(null); setAgregando(null); };

    const ejecutar = async (accion, mensajeOk) => {
        setGuardando(true);
        try {
            await accion();
            toast.success(mensajeOk);
            cerrarFormularios();
            await cargar();
            if (onChange) onChange();
        } catch (err) {
            toast.error(err.response?.data?.error || 'No se pudo guardar el cambio');
        } finally {
            setGuardando(false);
        }
    };

    const agregar = (tipo, datos) =>
        ejecutar(() => axiosInstance.post('/api/comidas/menu', { turno, tipo, ...datos }), 'Agregado al menú');

    const editar = (id, datos) =>
        ejecutar(() => axiosInstance.put(`/api/comidas/menu/${id}`, datos), 'Cambios guardados');

    const alternarActivo = (item) =>
        ejecutar(
            () => axiosInstance.put(`/api/comidas/menu/${item.id}`, { activo: !item.activo }),
            item.activo ? 'Ya no aparece en el formulario' : 'Vuelve a aparecer en el formulario'
        );

    const eliminar = (item) => {
        if (!window.confirm(`¿Eliminar "${item.nombre}" del menú? Las solicitudes que ya se hicieron no se ven afectadas.`)) return;
        ejecutar(() => axiosInstance.delete(`/api/comidas/menu/${item.id}`), 'Eliminado del menú');
    };

    const alternarCategoria = (cat, activo) =>
        ejecutar(
            () => axiosInstance.put('/api/comidas/menu/categoria', { turno, categoria: cat, activo }),
            activo ? `${cat} vuelve a aparecer en el formulario` : `${cat} ya no aparece en el formulario`
        );

    const eliminarCategoria = (cat, cantidad) => {
        const platos = cantidad === 1 ? 'su único plato' : `sus ${cantidad} platos`;
        if (!window.confirm(`¿Eliminar la categoría "${cat}" con ${platos}? Las solicitudes que ya se hicieron no se ven afectadas.`)) return;
        ejecutar(
            () => axiosInstance.delete('/api/comidas/menu/categoria', { params: { turno, categoria: cat } }),
            `${cat} eliminada del menú`
        );
    };

    const coincide = (item) => {
        if (soloSinPrecio && (item.tipo === 'refrigerio' ? !sinCombos(item) : tienePrecio(item))) return false;
        const q = normalizar(busqueda.trim());
        if (!q) return true;
        return [item.nombre, item.descripcion, item.categoria].some(t => normalizar(t).includes(q));
    };

    const renderItem = (item, seccion, categorias) => {
        if (editandoId === item.id) {
            return (
                <ItemForm key={item.id} turno={turno} tipo={seccion.tipo} categorias={categorias} inicial={item}
                    guardando={guardando} onGuardar={datos => editar(item.id, datos)} onCancelar={cerrarFormularios} />
            );
        }
        return (
            <div key={item.id}
                className={`flex items-center justify-between gap-3 px-4 py-3 rounded-xl border border-gray-100 transition-colors
                    ${item.activo ? 'bg-white hover:bg-gray-50' : 'bg-gray-50'}`}>
                <div className="min-w-0">
                    <p className={`text-sm font-medium truncate ${item.activo ? 'text-gray-900' : 'text-gray-400'}`}>{item.nombre}</p>
                    {item.descripcion && <p className="text-xs text-gray-500 truncate">{item.descripcion}</p>}
                    {item.tipo === 'refrigerio' && (
                        <div className="flex flex-wrap gap-1 mt-1">
                            {sinCombos(item)
                                ? <span className="text-xs text-gray-300 italic">Sin combos</span>
                                : item.combos.map(c => (
                                    <span key={c.unidades} className={`text-[11px] font-medium tabular-nums px-2 py-0.5 rounded-md ${item.activo ? 'bg-gray-100 text-gray-700' : 'bg-gray-100 text-gray-400'}`}>{textoCombo(c)}</span>
                                ))}
                        </div>
                    )}
                    {!item.activo && <span className="text-[10px] uppercase font-bold text-red-500 tracking-wider">Oculto</span>}
                </div>
                <div className="flex items-center gap-0.5 shrink-0">
                    {!TIPOS_CON_PRECIO.includes(item.tipo) ? null : tienePrecio(item) ? (
                        <span className={`text-sm font-semibold tabular-nums mr-2 ${item.activo ? 'text-gray-900' : 'text-gray-400'}`}>{formatoMonto(item.precio)}</span>
                    ) : (
                        <span className="text-xs text-gray-300 italic mr-2">Sin precio</span>
                    )}
                    <button type="button" onClick={() => alternarActivo(item)} disabled={guardando}
                        className={`${BTN_ICONO} ${item.activo ? 'hover:text-gray-700' : 'text-amber-600 hover:text-amber-700'}`}
                        title={item.activo ? 'Ocultar del formulario' : 'Volver a mostrar'}>
                        {item.activo ? <Eye size={14} /> : <EyeOff size={14} />}
                    </button>
                    <button type="button" onClick={() => { setEditandoId(item.id); setAgregando(null); }}
                        className={`${BTN_ICONO} hover:text-gray-700`} title="Editar">
                        <Pencil size={14} />
                    </button>
                    <button type="button" onClick={() => eliminar(item)} disabled={guardando}
                        className={`${BTN_ICONO} hover:text-red-500`} title="Eliminar">
                        <Trash2 size={14} />
                    </button>
                </div>
            </div>
        );
    };

    const renderSeccion = (seccion) => {
        const delTurno = items.filter(i => i.turno === turno && i.tipo === seccion.tipo);
        const visibles = delTurno.filter(coincide);
        const categorias = [...new Set(delTurno.map(i => i.categoria).filter(Boolean))];
        const agregandoAqui = agregando?.tipo === seccion.tipo;
        // Con una búsqueda activa la categoría se ve a medias: ocultarla o borrarla
        // afectaría platos que no están en pantalla.
        const buscando = busqueda.trim() !== '' || soloSinPrecio;
        const sinPiezasVisibles = seccion.tipo === 'pieza' && delTurno.length > 0 && delTurno.every(i => !i.activo);
        const sinRefrigeriosVisibles = seccion.tipo === 'refrigerio' && delTurno.length > 0 && delTurno.every(i => !i.activo);

        const formularioNuevo = (categoriaInicial) => (
            <ItemForm turno={turno} tipo={seccion.tipo} categorias={categorias}
                inicial={categoriaInicial ? { categoria: categoriaInicial } : null}
                guardando={guardando} onGuardar={datos => agregar(seccion.tipo, datos)} onCancelar={cerrarFormularios} />
        );

        return (
            <div key={seccion.tipo} className="bg-white rounded-2xl border border-gray-100 p-6 space-y-4">
                <div className="flex flex-wrap items-center justify-between gap-3">
                    <div>
                        <div className="flex items-center gap-2">
                            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wider">{seccion.titulo}</h3>
                            <span className="text-xs font-bold text-sura-blue bg-gray-50 px-2 py-1 rounded-md">{delTurno.length}</span>
                        </div>
                        <p className="text-xs text-sura-blue mt-1">{seccion.ayuda}</p>
                    </div>
                    <button type="button" onClick={() => { setAgregando({ tipo: seccion.tipo, categoria: '' }); setEditandoId(null); }} className={BTN_PRIMARIO}>
                        <Plus size={16} /> Agregar {seccion.singular}
                    </button>
                </div>

                {sinPiezasVisibles && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg">
                        Todas las piezas están ocultas: nadie puede pedir desayuno hasta que muestres al menos una.
                    </p>
                )}
                {sinRefrigeriosVisibles && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 px-3 py-2 rounded-lg">
                        Todos los refrigerios están ocultos: nadie puede pedir refrigerio hasta que muestres al menos uno.
                    </p>
                )}

                {agregandoAqui && !agregando.categoria && (
                    <div className="grid grid-cols-1">{formularioNuevo('')}</div>
                )}

                {delTurno.length === 0 && !agregandoAqui && (
                    <p className="text-sm text-gray-400 italic">Todavía no hay opciones aquí.</p>
                )}

                {delTurno.length > 0 && visibles.length === 0 && (
                    <p className="text-sm text-gray-400 italic">
                        {busqueda.trim()
                            ? `Nada coincide con "${busqueda}"${soloSinPrecio ? (seccion.tipo === 'refrigerio' ? ' sin combos' : ' sin precio') : ''}.`
                            : seccion.tipo === 'refrigerio' ? 'Todos los refrigerios ya tienen combos.' : 'Todas estas opciones ya tienen precio.'}
                    </p>
                )}

                {seccion.tipo === 'plato' ? (
                    categorias.map(cat => {
                        const deCategoria = visibles.filter(i => i.categoria === cat);
                        const todosDeCategoria = delTurno.filter(i => i.categoria === cat);
                        const categoriaOculta = todosDeCategoria.some(i => i.categoria_oculta);
                        const agregandoEnCategoria = agregandoAqui && agregando.categoria === cat;
                        if (deCategoria.length === 0 && !agregandoEnCategoria) return null;
                        return (
                            <div key={cat} className="space-y-2">
                                <div className="flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-2 min-w-0">
                                        <h4 className={`text-xs font-bold uppercase tracking-wider truncate ${categoriaOculta ? 'text-gray-400' : 'text-sura-blue'}`}>{cat}</h4>
                                        {categoriaOculta && <span className="shrink-0 text-[10px] uppercase font-bold text-red-500 tracking-wider">Oculta</span>}
                                    </div>
                                    <div className="flex items-center gap-0.5 shrink-0">
                                        <button type="button" onClick={() => { setAgregando({ tipo: seccion.tipo, categoria: cat }); setEditandoId(null); }}
                                            className="text-xs text-sura-blue hover:underline flex items-center gap-1 mr-2" title={`Añadir a ${cat}`}>
                                            <Plus size={12} /> Añadir
                                        </button>
                                        {!buscando && (
                                            <>
                                                <button type="button" onClick={() => alternarCategoria(cat, categoriaOculta)} disabled={guardando}
                                                    className={`${BTN_ICONO} ${categoriaOculta ? 'text-amber-600 hover:text-amber-700' : 'hover:text-gray-700'}`}
                                                    title={categoriaOculta ? 'Volver a mostrar la categoría completa' : 'Ocultar la categoría completa'}>
                                                    {categoriaOculta ? <EyeOff size={14} /> : <Eye size={14} />}
                                                </button>
                                                <button type="button" onClick={() => eliminarCategoria(cat, todosDeCategoria.length)} disabled={guardando}
                                                    className={`${BTN_ICONO} hover:text-red-500`} title="Eliminar la categoría completa">
                                                    <Trash2 size={14} />
                                                </button>
                                            </>
                                        )}
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                    {agregandoEnCategoria && formularioNuevo(cat)}
                                    {deCategoria.map(item => renderItem(item, seccion, categorias))}
                                </div>
                            </div>
                        );
                    })
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        {visibles.map(item => renderItem(item, seccion, categorias))}
                    </div>
                )}
            </div>
        );
    };

    if (loading) {
        return <div className="flex justify-center py-16"><Loader2 className="animate-spin text-gray-300" size={24} /></div>;
    }

    return (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
            <p className="text-sm text-gray-600">
                Administra lo que los empleados pueden pedir en cada turno. Los cambios se aplican al instante en el formulario de solicitud.
            </p>

            <PrecioDelivery />

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {TURNOS.map(({ id, icon, color }) => (
                    <StatCard key={id} title={id} icon={icon} color={color}
                        value={resumen[id].total}
                        subtext={[
                            resumen[id].ocultos > 0 ? `${resumen[id].ocultos} ocultas` : null,
                            resumen[id].sinPrecio > 0 ? `${resumen[id].sinPrecio} ${id === 'Refrigerio' ? 'sin combos' : 'sin precio'}` : null,
                        ].filter(Boolean).join(' · ') || null}
                        active={turno === id}
                        onClick={() => { setTurno(id); setBusqueda(''); setSoloSinPrecio(false); cerrarFormularios(); }} />
                ))}
            </div>

            {resumen[turno].sinPrecio > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
                    {turno === 'Refrigerio' ? (
                        <span>
                            <strong className="text-gray-900">{resumen[turno].sinPrecio}</strong>{' '}
                            {resumen[turno].sinPrecio === 1 ? 'refrigerio' : 'refrigerios'} sin combos.
                            Se pueden pedir, pero no podrás armar su pedido hasta cargarles al menos un combo.
                        </span>
                    ) : (
                    <span>
                        <strong className="text-gray-900">{resumen[turno].sinPrecio}</strong>{' '}
                        {resumen[turno].sinPrecio === 1 ? 'opción' : 'opciones'} de {turno.toLowerCase()} sin precio
                        <span className="text-gray-400"> (platos, piezas y bebidas; los contornos y rellenos no llevan precio)</span>.
                        Mientras falten, el gasto de esas solicitudes queda incompleto.
                    </span>
                    )}
                    <button type="button" onClick={() => setSoloSinPrecio(v => !v)}
                        className={`shrink-0 px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ${soloSinPrecio ? 'bg-sura-blue text-white border-sura-blue' : 'bg-white text-sura-blue border-gray-200 hover:border-sura-blue'}`}>
                        {soloSinPrecio ? 'Ver todo' : turno === 'Refrigerio' ? 'Ver solo sin combos' : 'Ver solo sin precio'}
                    </button>
                </div>
            )}

            <div className="relative max-w-md">
                <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
                <input value={busqueda} onChange={e => setBusqueda(e.target.value)}
                    placeholder={`Buscar en ${turno.toLowerCase()}...`}
                    className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-xl text-sm focus:ring-2 focus:ring-gray-50 focus:border-sura-blue" />
            </div>

            {SECCIONES[turno].map(renderSeccion)}
        </motion.div>
    );
};

export default MenuComidaAdmin;
